import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import * as moment from 'moment';
import { AuthService } from 'src/app/service/auth.service';
import { ReportApiService } from 'src/app/shared/services/customer-api/report-api.service';
import { QueryApiService } from 'src/app/shared/services/customer-api/query-api.service';
import { ConfigService } from 'src/app/service/config.service';
import { ReportFeedbackService } from 'src/app/shared/services/report-feedback.service';
import {
  DialogReportComponent,
  ReportDialogData,
} from 'src/app/micro-components/dialog-report/dialog-report.component';
import getScoreTypeDetails from 'src/app/shared/utils/score-type-details';
import {
  ReportType,
  hasEnoughCompaniesForReport,
} from 'src/app/shared/utils/reports';
import {
  INITIAL_REPORT_TYPE_STATE,
  REPORT_REGISTRY,
  ReportDefinition,
  ReportDeps,
  ReportRegistryCtx,
  ReportTypeState,
  isStatusAvailable,
  scopeOfContext,
} from './report-state.registry';

export type ReportEntryId = ReportType | 'score';

export interface ReportEntry {
  id: ReportEntryId;
  menuTitle: string;
  disabled$: Observable<boolean>;
}

export interface EntityReportContext {
  mode: 'entity';
  entity: { type: string; alpha_id: string; name: string };
  folio: { name: string; content: string[] } | null;
  customScore: string;
}

export interface QueryReportContext {
  mode: 'query';
}

export type ReportContext = EntityReportContext | QueryReportContext;

export interface ReportBoxVm {
  loading: boolean;
  reports: ReportEntry[];
  portfolioInsufficientCompanies: boolean;
}

@Injectable({ providedIn: 'root' })
export class ReportStateService {
  // Score state — kept hand-rolled because score is multi-state (button-visible,
  // score-type, investment-disabled gate) and score-type-dependent.
  private isReportButtonVisibleSubject = new BehaviorSubject<boolean>(true);
  private isLoadingReportSubject = new BehaviorSubject<boolean>(false);
  private isReportAvailableSubject = new BehaviorSubject<boolean>(false);
  private reportStatusSubject = new BehaviorSubject<string | null>(null);
  private reportScoreTypeSubject = new BehaviorSubject<string | null>(null);
  private portfolioInsufficientCompaniesSubject = new BehaviorSubject<boolean>(
    false,
  );

  isReportButtonVisible$ = this.isReportButtonVisibleSubject.asObservable();
  isLoadingReport$ = this.isLoadingReportSubject.asObservable();
  isReportAvailable$ = this.isReportAvailableSubject.asObservable();
  reportStatus$ = this.reportStatusSubject.asObservable();
  reportScoreType$ = this.reportScoreTypeSubject.asObservable();
  portfolioInsufficientCompanies$ =
    this.portfolioInsufficientCompaniesSubject.asObservable();

  // Registry-driven state — one slot per (id, scope) row.
  private readonly registryStates = new Map<
    string,
    BehaviorSubject<ReportTypeState>
  >();

  constructor(
    private auth: AuthService,
    private reportApi: ReportApiService,
    private queryApi: QueryApiService,
    private config: ConfigService,
    private dialog: MatDialog,
    private reportFeedback: ReportFeedbackService,
  ) {
    for (const def of REPORT_REGISTRY) {
      this.registryStates.set(
        this.stateKey(def),
        new BehaviorSubject<ReportTypeState>(INITIAL_REPORT_TYPE_STATE),
      );
    }
  }

  // Public entry: callers pass the full ctx, the service runs the score check
  // (entity mode only) and the registry checks for the matching scope.
  checkStatuses(ctx: ReportContext): void {
    if (ctx.mode === 'entity') {
      this.runScoreCheck(ctx);
    }

    const matchingDefs = this.matchingDefs(ctx);

    for (const def of matchingDefs) {
      this.setState(def, { ...INITIAL_REPORT_TYPE_STATE, loading: true });
    }

    if (this.config.appConfig.setup.reportsDisabled) {
      for (const def of matchingDefs) this.setState(def, { loading: false });
      return;
    }

    const deps = this.buildDeps();
    const ctxData = this.toRegistryCtx(ctx);

    for (const def of matchingDefs) {
      def.checkStatus(ctxData, deps).subscribe({
        next: (status) =>
          this.setState(def, {
            status,
            available: isStatusAvailable(status),
            loading: false,
          }),
        error: () => this.setState(def, { loading: false }),
      });
    }
  }

  getViewModel$(ctx$: Observable<ReportContext>): Observable<ReportBoxVm> {
    return ctx$.pipe(switchMap((ctx) => this.buildVm$(ctx)));
  }

  dispatchReport(id: ReportEntryId, ctx: ReportContext): void {
    if (id === 'score') {
      if (ctx.mode === 'query') return;
      this.openScoreDialog(ctx);
      return;
    }
    const def = this.matchingDefs(ctx).find((d) => d.id === id);
    if (!def) return;
    const state = this.getStateSubject(def).getValue();
    const data = def.buildDialogData(
      this.toRegistryCtx(ctx),
      state,
      this.buildDeps(),
    );
    // Query reports are single-type with no recipients or scheduling options,
    // so the dialog would add no choices — generate directly on click.
    if (ctx.mode === 'query' && data.searchQuery) {
      this.sendQueryReportNow(data);
      return;
    }
    this.dialog.open(DialogReportComponent, {
      restoreFocus: false,
      panelClass: 'score-report-dialog',
      data,
    });
  }

  // One-click query report: sends to the user's own account (report-api adds
  // the caller's email), no dialog. Delivery is an emailed download link.
  private sendQueryReportNow(data: ReportDialogData): void {
    const date = moment(new Date()).format('YYYY-MM-DD');
    this.reportApi
      .sendQueryReport(
        data.searchQuery!,
        data.queryName!,
        data.reportType,
        date,
      )
      .subscribe({
        next: () => this.reportFeedback.reportSent(),
        error: () => this.reportFeedback.reportError(),
      });
  }

  clearState(): void {
    this.isReportButtonVisibleSubject.next(true);
    this.isLoadingReportSubject.next(false);
    this.isReportAvailableSubject.next(false);
    this.reportStatusSubject.next(null);
    this.reportScoreTypeSubject.next(null);
    this.portfolioInsufficientCompaniesSubject.next(false);
    for (const subj of this.registryStates.values()) {
      subj.next(INITIAL_REPORT_TYPE_STATE);
    }
  }

  // ---- registry plumbing ----

  private stateKey(def: ReportDefinition): string {
    return `${def.id}::${def.scope}`;
  }

  private getStateSubject(
    def: ReportDefinition,
  ): BehaviorSubject<ReportTypeState> {
    return this.registryStates.get(this.stateKey(def))!;
  }

  private setState(
    def: ReportDefinition,
    partial: Partial<ReportTypeState>,
  ): void {
    const subj = this.getStateSubject(def);
    subj.next({ ...subj.getValue(), ...partial });
  }

  private matchingDefs(ctx: ReportContext): ReportDefinition[] {
    const scope = scopeOfContext(
      ctx.mode,
      ctx.mode === 'entity' ? ctx.entity?.type : undefined,
    );
    return scope === null
      ? []
      : REPORT_REGISTRY.filter((d) => d.scope === scope);
  }

  private buildDeps(): ReportDeps {
    return {
      reportApi: this.reportApi,
      queryApi: this.queryApi,
      date: moment(new Date()).format('YYYY-MM-DD'),
    };
  }

  private toRegistryCtx(ctx: ReportContext): ReportRegistryCtx {
    if (ctx.mode === 'query') return {};
    return { entity: ctx.entity, folio: ctx.folio };
  }

  // ---- view model ----

  private buildVm$(ctx: ReportContext): Observable<ReportBoxVm> {
    const matchingDefs = this.matchingDefs(ctx);

    const score$: Observable<ReportEntry | null> =
      ctx.mode === 'entity' ? this.buildScoreEntry$(ctx) : of(null);

    const registryEntries$: Observable<(ReportEntry | null)[]> =
      matchingDefs.length === 0
        ? of([])
        : combineLatest(
            matchingDefs.map((def) => this.buildRegistryEntry$(def)),
          );

    const entries$ = combineLatest([score$, registryEntries$]).pipe(
      map(([score, registryEntries]) =>
        [score, ...registryEntries].filter((e): e is ReportEntry => e !== null),
      ),
    );

    const registryLoading$: Observable<boolean> =
      matchingDefs.length === 0
        ? of(false)
        : combineLatest(
            matchingDefs.map((def) =>
              this.getStateSubject(def).pipe(map((s) => s.loading)),
            ),
          ).pipe(map((arr) => arr.some(Boolean)));

    const loading$ =
      ctx.mode === 'query'
        ? registryLoading$
        : combineLatest([this.isLoadingReport$, registryLoading$]).pipe(
            map(([a, b]) => a || b),
          );

    const portfolioInsufficientCompanies$ =
      ctx.mode === 'entity' && ctx.entity?.type === 'portfolio'
        ? this.portfolioInsufficientCompanies$
        : of(false);

    return combineLatest([
      entries$,
      loading$,
      portfolioInsufficientCompanies$,
    ]).pipe(
      map(([reports, loading, portfolioInsufficientCompanies]) => ({
        loading,
        reports,
        portfolioInsufficientCompanies,
      })),
    );
  }

  private buildRegistryEntry$(
    def: ReportDefinition,
  ): Observable<ReportEntry | null> {
    return this.getStateSubject(def).pipe(
      map((state) =>
        state.available
          ? {
              id: def.id,
              menuTitle: def.menuTitle,
              disabled$: of(false),
            }
          : null,
      ),
    );
  }

  private buildScoreEntry$(
    ctx: EntityReportContext,
  ): Observable<ReportEntry | null> {
    const entityType = ctx.entity?.type;
    if (entityType !== 'company' && entityType !== 'portfolio') {
      return of(null);
    }

    // Company: show only when score-report is available.
    // Portfolio: show whenever the report button is allowed (button visible);
    //   investment-disabled and not-yet-available surface via disabled$.
    const visible$ =
      entityType === 'company'
        ? this.isReportAvailable$
        : this.isReportButtonVisible$;

    const disabled$ =
      entityType === 'portfolio'
        ? combineLatest([
            this.isReportAvailable$,
            this.portfolioInsufficientCompanies$,
          ]).pipe(map(([avail, disabled]) => !avail || disabled))
        : of(false);

    return visible$.pipe(
      map((visible) =>
        visible
          ? {
              id: 'score' as const,
              menuTitle: 'Score Report (score trend/ top movers & peergroup)',
              disabled$,
            }
          : null,
      ),
    );
  }

  // ---- score check (unchanged behaviour, just renamed from checkReportStatus) ----

  private runScoreCheck(ctx: EntityReportContext): void {
    const { entity, folio, customScore } = ctx;
    this.isReportButtonVisibleSubject.next(true);
    this.isReportAvailableSubject.next(false);
    this.isLoadingReportSubject.next(true);
    this.portfolioInsufficientCompaniesSubject.next(false);

    const date = moment(new Date()).format('YYYY-MM-DD');
    const reportScoreType = getScoreTypeDetails(customScore).apiType;
    this.reportScoreTypeSubject.next(reportScoreType);

    if (this.config.appConfig.setup.reportsDisabled) {
      this.isReportButtonVisibleSubject.next(false);
      this.isLoadingReportSubject.next(false);
      return;
    }

    if (entity?.type === 'company') {
      const effectiveScoreType =
        reportScoreType && reportScoreType !== 'investment'
          ? reportScoreType
          : (this.wireOf(this.COMPANY_SCORE_KEYS[0]) ?? 'credit_watch');
      this.reportApi
        .getReportStatus(entity.alpha_id, effectiveScoreType, date)
        .subscribe(
          (res) => {
            this.reportStatusSubject.next(res['status']);
            this.isReportAvailableSubject.next(
              res['status'] !== 'not_enough_data',
            );
            this.isLoadingReportSubject.next(false);
          },
          () => this.isLoadingReportSubject.next(false),
        );
    } else if (entity?.type === 'portfolio') {
      if (!folio || !(folio.content?.length > 0)) {
        this.isReportButtonVisibleSubject.next(false);
        this.isLoadingReportSubject.next(false);
        return;
      }
      const portfolio = folio.name;
      const companies = folio.content.filter(
        (e) => e.split(':')[0] === 'company',
      );
      const insufficientCompanies = !hasEnoughCompaniesForReport(folio.content);
      this.portfolioInsufficientCompaniesSubject.next(insufficientCompanies);
      if (insufficientCompanies) {
        this.isReportAvailableSubject.next(true);
        this.isLoadingReportSubject.next(false);
        return;
      }
      const effectiveScoreType =
        reportScoreType ??
        this.wireOf(this.PORTFOLIO_SCORE_KEYS[0]) ??
        'credit_watch';
      this.reportApi
        .getReportStatusPortfolio(
          portfolio,
          companies,
          effectiveScoreType,
          date,
        )
        .subscribe(
          (res) => {
            this.reportStatusSubject.next(res['status']);
            if (res['status'] !== 'not_enough_data') {
              this.isReportAvailableSubject.next(true);
            }
            this.isLoadingReportSubject.next(false);
          },
          () => this.isLoadingReportSubject.next(false),
        );
    } else {
      this.isReportButtonVisibleSubject.next(false);
      this.isLoadingReportSubject.next(false);
    }
  }

  // ---- score dialog ----

  // Menu entries are cockpit score ids. They get converted to the wire ReportType
  // (apiType) only when calling the API or populating dialog data, so label
  // lookups stay on the canonical `id → details` path (no reverse index).
  private readonly COMPANY_SCORE_KEYS: string[] = [
    'credit_risk',
    'general_risk',
    'immediate_risk',
    'esg',
    'esg_risk',
    'eb_sim',
    'eb_sim_risk',
  ];

  private readonly PORTFOLIO_SCORE_KEYS: string[] = [
    ...this.COMPANY_SCORE_KEYS,
    'bb',
  ];

  private wireOf(scoreKey: string): ReportType | null {
    const apiType = getScoreTypeDetails(scoreKey).apiType;
    return (apiType as ReportType | null) ?? null;
  }

  private buildScoreTypeOptions(
    scoreKeys: string[],
    currentScoreKey: string | null,
  ): { value: ReportType; label: string }[] {
    const toOption = (
      k: string,
    ): { value: ReportType; label: string } | null => {
      const d = getScoreTypeDetails(k);
      if (!d.apiType) return null;
      return {
        value: d.apiType as ReportType,
        label: d.reportLabel || d.label || k,
      };
    };
    const list = scoreKeys
      .map(toOption)
      .filter((x): x is { value: ReportType; label: string } => x !== null);
    if (currentScoreKey) {
      // Dedup by the wire `value` (apiType) the option resolves to, so the
      // current score is only prepended when its wire type isn't already listed
      // — robust to which cockpit id (e.g. credit_risk) produced it.
      const extra = toOption(currentScoreKey);
      if (extra && !list.some((o) => o.value === extra.value)) {
        list.unshift(extra);
      }
    }
    return list;
  }

  private openScoreDialog(ctx: EntityReportContext): void {
    if (this.portfolioInsufficientCompaniesSubject.getValue()) {
      return;
    }
    const { entity, folio, customScore } = ctx;
    // Resolve fresh from ctx.customScore so the dropdown defaults to the user's
    // currently-selected score. Company branch excludes the Investment wire
    // (cockpit `bb`/`bull_bear`) because Investment is portfolio-only.
    const customWire = this.wireOf(customScore);
    const fallbackKey =
      entity.type === 'portfolio'
        ? this.PORTFOLIO_SCORE_KEYS[0]
        : this.COMPANY_SCORE_KEYS[0];
    const reportScoreType: ReportType =
      entity.type === 'portfolio'
        ? (customWire ?? (this.wireOf(fallbackKey) as ReportType))
        : customWire && customWire !== 'investment'
          ? customWire
          : (this.wireOf(fallbackKey) as ReportType);
    if (entity.type === 'portfolio') {
      if (!folio) return;
      this.dialog.open(DialogReportComponent, {
        restoreFocus: false,
        panelClass: 'score-report-dialog',
        data: {
          reportType: reportScoreType,
          entityId: entity.alpha_id,
          entityType: entity.type,
          portfolioName: entity.name,
          portfolioUid:
            (folio as { uid?: string } | undefined)?.uid ?? entity.alpha_id,
          portfolioContent: folio.content.filter(
            (e) => e.split(':')[0] === 'company',
          ),
          titleLine1: entity.name,
          titleLine2:
            (getScoreTypeDetails(customScore).reportLabel ||
              getScoreTypeDetails(customScore).label) + ' Portfolio Report',
          messageLine:
            folio.content.filter((e) => e.split(':')[0] === 'company')
              .length !== folio.content.length
              ? "Your portfolio includes entities other than companies that won't be covered by the report."
              : undefined,
          status: this.reportStatusSubject.getValue(),
          reportKind: 'score',
          scoreTypeOptions: this.buildScoreTypeOptions(
            this.PORTFOLIO_SCORE_KEYS,
            customScore,
          ),
        },
      });
    } else {
      this.dialog.open(DialogReportComponent, {
        restoreFocus: false,
        panelClass: 'score-report-dialog',
        data: {
          reportType: reportScoreType,
          entityId: entity.alpha_id,
          entityType: entity.type,
          titleLine1: entity.name,
          titleLine2: this.auth.getScoreName().name + ' Report',
          status: this.reportStatusSubject.getValue(),
          reportKind: 'score',
          scoreTypeOptions: this.buildScoreTypeOptions(
            this.COMPANY_SCORE_KEYS,
            customScore,
          ),
        },
      });
    }
  }
}
